import { Phone, Mail, MapPin } from 'lucide-react';
import { locations as allLocations, REGION_ORDER } from '@/data/locations';

const practiceAreas = [
  { label: 'Estate Planning', href: '/estate-planning/' },
  { label: 'Probate', href: '/probate/' },
  // 🔴 Один анкор — один адрес. В шапке «Wills & Trusts» ведёт на
  // /wills-and-trusts/, а в подвале вёл на /trusts/: одна и та же надпись на
  // каждой из 5 484 страниц указывала на две разные страницы, и обе борются за
  // один запрос. Найдено аудитом 03.09.2026.
  // ⚠️ Это правка ПРОТИВОРЕЧИЯ, а не склейки: /trusts/ («Wills & Trusts Attorney
  // NYC | Revocable & Irrevocable») и /wills-and-trusts/ по-прежнему целятся в
  // один интент, и какая из них выживет — решается по данным GSC, не раньше
  // октября вместе с прополкой близнецов.
  { label: 'Wills & Trusts', href: '/wills-and-trusts/' },
  { label: 'Asset Protection', href: '/asset-protection/' },
  { label: 'Guardianship', href: '/guardianship/' },
  { label: 'Power of Attorney', href: '/power-of-attorney/' },
  { label: 'Elder Law', href: '/elder-law/' },
  { label: 'Family Law', href: '/family-law/' },
  { label: 'Real Estate', href: '/real-estate/' },
  { label: 'Personal Injury', href: '/personal-injury/' },
];

const quickLinks: { label: string; href: string; external?: boolean }[] = [
  { label: 'About Us', href: '/about-us/' },
  { label: 'Locations', href: '/location/' },
  { label: 'Our Attorneys', href: '/attorneys/' },
  // Два анкора переехали сюда из выпадающего меню, снятого вслед за
  // morganlegalgroup. Это единственные общесайтовые ссылки с текстом
  // «Estate Planning Attorney NYC» и «Estate Planning Lawyer NYC» — запросами,
  // по которым ранжируется главная. Снять их вместе с меню значило бы убрать
  // сигнал молча.
  { label: 'Estate Planning Attorney NYC', href: '/nyc-estate-planning-3/' },
  { label: 'Estate Planning Lawyer NYC', href: '/russel-morgan-estate-planning-lawyer-in-nyc/' },
  { label: 'Our Fees', href: '/our-fees/' },
  { label: 'Reviews', href: '/reviews/' },
  // 🔴 С 03.09.2026 «Blog» и есть вход в перенесённые статьи: указатель
  // библиотеки переехал с /articles/ на /blog/. Прежде это были ДВА пункта на
  // две страницы, и /blog/ вёл на страницу движка, которой в день его снятия
  // не станет. Без этого входа 5 242 статьи недостижимы из навигации и не
  // будут обойдены. → scripts/check-connectivity.js
  { label: 'Blog', href: '/blog/' },
  { label: 'Publications', href: '/publications/' },
  { label: 'Resources', href: '/resources/' },
  { label: 'Contact Us', href: '/contact-us/' },
  { label: 'Free Consultation', href: '/appointment/' },
  // Комната Lawyer.com. Была в футере прежней версии и пропала при
  // переезде на статику; в шапке она только на широких экранах
  // (utility strip скрыт до lg), так что на телефоне это
  // единственный вход в неё.
  { label: 'Video Conference', href: 'https://morgan.lawyer.com/', external: true },
  // Приём платежей. Прежний адрес из архива (morganlegalgroup.com/pay-online/)
  // восстанавливать было нельзя: он отвечает 200, но редиректом на /contact/ —
  // страницы оплаты там больше нет. Действующий адрес дал владелец 31.08.2026.
  { label: 'Pay Online', href: 'https://secure.lawpay.com/pages/morganlegalny/operating', external: true },
];

/**
 * 🔴 Список ВЫВОДИТСЯ из locations.ts, а не переписывается сюда руками.
 *
 * До 04.09.2026 здесь лежала своя копия на шесть строк, параллельная данным на
 * восемнадцать. Обе были верны по отдельности и разошлись молча: шесть узлов
 * получали сквозную ссылку с каждой из 5 484 страниц, а двенадцать — ровно одну,
 * с /location/. Среди этих двенадцати /westchester/ (11 021 показ спроса за 90
 * дней, позиция 30.9) и /manhasset-ny/ (16 582 показа). Сторож связности видел
 * их и печатал «single-link 21», но флаг --warn-single никто не включал, а
 * «сирот 0» читалось как «со связностью всё хорошо».
 *
 * Вывод из данных убирает не расхождение, а ВОЗМОЖНОСТЬ расхождения: новый узел
 * получает сквозную ссылку самим фактом появления в locations.ts.
 * Порядок — REGION_ORDER, подпись — shortName. Проверяется
 * scripts/check-location-links.mjs.
 */
const locations = REGION_ORDER.flatMap((region) =>
  allLocations.filter((l) => l.region === region).map((l) => ({ label: l.shortName, href: l.url })),
);

export default function Footer() {
  return (
    <footer className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <picture>
              <source srcSet="/brand/morgan-logo.webp" type="image/webp" />
              <img
                src="/brand/morgan-logo.png"
                loading="lazy"
                decoding="async"
                alt="Morgan Legal Group · Attorneys at Law"
                width="143"
                height="52"
                className="h-12 w-auto mb-4 opacity-90"
              />
            </picture>
            <div className="text-gold text-xs tracking-[0.2em] uppercase mb-5">NYC &middot; Estate Planning Since 2017</div>
            <address className="not-italic font-body text-sm text-white/70 leading-relaxed mb-4">
              <div className="flex items-start gap-2 mb-2">
                <MapPin className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <span>15 Maiden Ln #905<br />New York, NY 10038</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                <a href="tel:8885291315" className="hover:text-gold transition-colors">888-529-1315</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                <a href="mailto:contact@morganlegalny.com" className="hover:text-gold transition-colors">
                  contact@morganlegalny.com
                </a>
              </div>
            </address>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-[0.2em] text-gold mb-5">Practice Areas</h4>
            <ul className="space-y-2 font-body text-sm">
              {practiceAreas.map((p) => (
                <li key={p.href}>
                  <a href={p.href} className="text-white/70 hover:text-gold transition-colors">
                    {p.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-[0.2em] text-gold mb-5">Quick Links</h4>
            <ul className="space-y-2 font-body text-sm">
              {quickLinks.map((q) => (
                <li key={q.href}>
                  <a
                    href={q.href}
                    {...(q.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="text-white/70 hover:text-gold transition-colors"
                  >
                    {q.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-body text-xs uppercase tracking-[0.2em] text-gold mb-5">Locations Served</h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 font-body text-sm">
              {locations.map((loc) => (
                <li key={loc.href}>
                  <a href={loc.href} className="text-white/70 hover:text-gold transition-colors">
                    {loc.label}
                  </a>
                </li>
              ))}
              <li className="col-span-2">
                <a href="/location/" className="text-gold hover:text-white transition-colors font-medium">
                  All Locations
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Service-area copy carried over verbatim from the WordPress footer: it is
            site-wide long-tail local coverage (Queens neighbourhoods, Nassau towns,
            Westchester) that the React footer did not have. */}
        <div className="border-t border-white/10 mt-12 pt-8 text-xs font-body text-white/50 leading-relaxed">
          <p className="mb-4">
            <span className="text-white/70">Serving all of New York.</span> Helping each and every of New
            York&rsquo;s local cities and counties including, without limitation: New York City including New York
            County (Manhattan); Bronx County; Kings County (Brooklyn); Queens County including Astoria, Bayside,
            Flushing, Forest Hills, Fresh Meadows, Kew Gardens, Long Island City, Middle Village, Queens Village,
            Rego Park, Ridgewood, Whitestone, Woodhaven, and Woodside; Richmond County (Staten Island); and Nassau
            County including Garden City, Great Neck, Levittown, New Hyde Park, Syosset, and Westbury; Suffolk
            County; and Westchester County including New Rochelle and Yonkers.
          </p>
          <p className="mb-3">
            <strong className="text-white/70">Attorney Advertising.</strong> Prior results do not guarantee a similar outcome. The
            information provided on this website is for general informational purposes only and is not legal advice. No attorney-client
            relationship is formed by viewing this site or contacting Morgan Legal Group, P.C.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span>&copy; {new Date().getFullYear()} Morgan Legal Group, P.C. All rights reserved.</span>
            <a href="/privacy-policy-page/" className="hover:text-gold">Privacy Policy</a>
            <a href="/disclaimer/" className="hover:text-gold">Disclaimer</a>
            <a href="/glossary-of-legal-terms/" className="hover:text-gold">Glossary</a>
            <a href="/sitemap.xml" className="hover:text-gold">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
